/**
 * Tabs Component
 * Handles tab navigation and content switching with full accessibility support
 */

class TabsSection {
  constructor(element) {
    this.element = element;
    this.tabButtons = this.element.querySelectorAll("[data-tab-button]");
    this.tabPanels = this.element.querySelectorAll("[data-tab-panel]");
    this.currentTabIndex = 0;

    // Find the currently active tab
    this.tabButtons.forEach((button, index) => {
      if (button.classList.contains("tabs-nav__button--active")) {
        this.currentTabIndex = index;
      }
    });

    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchToTab(index);
      });

      button.addEventListener("keydown", (e) => {
        this.handleKeydown(e, index);
      });
    });
  }

  setInitialState() {
    // Ensure only the active tab and panel are visible
    this.updateActiveStates();
  }

  switchToTab(newIndex) {
    if (
      newIndex === this.currentTabIndex ||
      newIndex < 0 ||
      newIndex >= this.tabButtons.length
    ) {
      return;
    }

    this.currentTabIndex = newIndex;
    this.updateActiveStates();

    // Focus the newly active tab for accessibility
    this.tabButtons[newIndex].focus();

    // Dispatch custom event for other components that might need to know about tab changes
    this.element.dispatchEvent(
      new CustomEvent("tabs:changed", {
        detail: {
          activeIndex: newIndex,
          activeTab: this.tabButtons[newIndex],
          activePanel: this.tabPanels[newIndex],
        },
      })
    );
  }

  updateActiveStates() {
    this.tabButtons.forEach((button, index) => {
      const isActive = index === this.currentTabIndex;

      button.classList.toggle("tabs-nav__button--active", isActive);
      button.setAttribute("aria-selected", isActive.toString());
      button.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    this.tabPanels.forEach((panel, index) => {
      const isActive = index === this.currentTabIndex;

      panel.classList.toggle("tabs-content__panel--active", isActive);

      if (isActive) {
        panel.removeAttribute("aria-hidden");
      } else {
        panel.setAttribute("aria-hidden", "true");
      }
    });
  }

  handleKeydown(event, currentIndex) {
    let newIndex = currentIndex;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        newIndex =
          currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
        break;

      case "ArrowRight":
        event.preventDefault();
        newIndex =
          currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
        break;

      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;

      case "End":
        event.preventDefault();
        newIndex = this.tabButtons.length - 1;
        break;

      default:
        return;
    }

    this.switchToTab(newIndex);
  }

  // Public API for external control
  goToTab(index) {
    this.switchToTab(index);
  }

  getCurrentTab() {
    return this.currentTabIndex;
  }

  getTotalTabs() {
    return this.tabButtons.length;
  }
}

// Auto-initialize tabs sections
function initializeTabs() {
  const tabsSections = document.querySelectorAll("[data-tabs-section]");

  tabsSections.forEach((section) => {
    if (!section.tabsInstance) {
      section.tabsInstance = new TabsSection(section);
    }
  });
}

// Initialize on DOM content loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeTabs);
} else {
  initializeTabs();
}

// Re-initialize for dynamically added content (like in theme editor)
document.addEventListener("shopify:section:load", (event) => {
  const section = event.target;
  const tabsSection = section.querySelector("[data-tabs-section]");

  if (tabsSection && !tabsSection.tabsInstance) {
    tabsSection.tabsInstance = new TabsSection(tabsSection);
  }
});

// Clean up when section is unloaded
document.addEventListener("shopify:section:unload", (event) => {
  const section = event.target;
  const tabsSection = section.querySelector("[data-tabs-section]");

  if (tabsSection && tabsSection.tabsInstance) {
    tabsSection.tabsInstance = null;
  }
});

// Export for potential external use
window.TabsSection = TabsSection;
